import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useRef } from 'react';

import { Button } from '$partials/ui';
import { Dispatcher as IconDispatcher } from '$partials/icon';
import { IconUniPlan } from '$partials/icon/icons';
import { Stream as ModuleStream } from '$partials/modules';
import { useLanguage } from '$contexts/language.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Search = forwardRef(({
    className    = null,
    isOpen       = null,
    modules      = null,
    toggleDrawer = () => {}
}, searchButtonRef) => {
    // references
    const drawerRef = useRef();
    const searchRef = useRef();

    // methods
    const handleClick = (e) => {
        if (drawerRef.current && drawerRef.current === e.target) {
            toggleDrawer(e, null);
        }
    };

    // hooks
    const {
        current: localeCurrent,
        translate: t
    } = useLanguage();

    useEffect(() => {
        if (drawerRef.current) {
            drawerRef.current.addEventListener('click', handleClick, { passive: false });
        }

        return () => {
            if (drawerRef.current) {
                drawerRef.current.removeEventListener('click', handleClick);
            }
        };
    }, []);

    useEffect(() => {
        if (searchRef.current) {
            if (isOpen) {
                disableBodyScroll(searchRef.current);
            } else {
                enableBodyScroll(searchRef.current);
            }
        }
    }, [ isOpen ]);

    // render
    return (
        <div
            className={`search${isOpen ? ' search--is-open ' : ' '}${className}`}
            ref={searchRef}>
            {/* button */}
            <Button
                action={e => toggleDrawer(e, 'search')}
                aria-label={t('Open search')}
                ref={searchButtonRef}>
                <span className='accessible-hide'>
                    {t('Open search')}
                </span>

                <span>
                    <IconDispatcher name='IconSearch' />
                </span>
            </Button>

            <div
                className='search__drawer side-bar black'
                ref={drawerRef}>
                <div className='side-bar__aside md-height-60 bg-lemon-yellow-crayola'>
                    <>
                        <div className='navigation'>
                            <Button
                                action={e => toggleDrawer(e, 'navigation')}
                                aria-label={t('Open navigation')}>
                                <span className='accessible-hide'>
                                    {t('Open navigation')}
                                </span>

                                <IconDispatcher name='IconBurger' />
                            </Button>
                        </div>

                        <div className='search'>
                            <Button
                                action={e => toggleDrawer(e, 'search')}
                                aria-label={t('Close search')}>
                                <span className='accessible-hide'>
                                    {t('Close search')}
                                </span>

                                <IconDispatcher name='IconCross' />
                            </Button>
                        </div>

                        <a
                            className='block line-height-0 black'
                            href={`/${localeCurrent === 'en' ? '' : localeCurrent}`}>
                            <IconUniPlan />
                        </a>
                    </>
                </div>

                <div className='side-bar__content md-height-60'>
                    <div className='height-100 overflow-x-hidden overflow-y-auto bg-lemon-yellow-crayola'>
                        <div className='container'>
                            <div className='mln8 sm-mln9 md-ml0'>
                                <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                                    <div className='flex flex-wrap'>
                                        {/* side title */}
                                        <div className='absolute top-0 md-relative md-col-2 hide md-show sm-pt10 md-pt13'>
                                            <div className='px-gutter'>
                                                <p className='type-style-5 side-title side-title--lesser-top'>
                                                    {t('Explore')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* content / search */}
                                        <div className='col-12 sm-col-12 md-col-10 pt10 sm-pt10 md-pt13'>
                                            <div className='ml9 sm-ml9 md-ml0'>
                                                <div className='px-gutter'>
                                                    <form
                                                        action={`/${localeCurrent === 'en' ? '' : localeCurrent + '/'}search`}
                                                        className='mb10'>
                                                        <fieldset>
                                                            <label
                                                                className='form-field-reset block mb3 type-style-3 capitalise'
                                                                htmlFor='frm-search'>
                                                                {t('Search')}
                                                            </label>

                                                            <div className='flex items-center justify-between border-bottom border-top border-black'>
                                                                <input
                                                                    className='form-field-reset width-100 pb3 pt4 type-style-6'
                                                                    id='frm-search'
                                                                    maxLength={100}
                                                                    minLength={3}
                                                                    name='term'
                                                                    placeholder='Type to search'
                                                                    required={true}
                                                                    type='text'/>

                                                                <Button
                                                                    aria-label={t('Submit search')}
                                                                    className='mrn3'
                                                                    type='submit'>
                                                                    <span className='accessible-hide'>
                                                                        {t('Submit search')}
                                                                    </span>

                                                                    <IconDispatcher name='IconSearch' />
                                                                </Button>
                                                            </div>
                                                        </fieldset>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* modules */}
                            <ModuleStream
                                classes='mln8 sm-mln9 md-mln0 md-mb7'
                                modules={modules}
                                namespace='search-modal' />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

Search.displayName = 'Search';

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    className    : PropTypes.string,
    isOpen       : PropTypes.bool.isRequired,
    modules      : PropTypes.array,
    toggleDrawer : PropTypes.func.isRequired
};

Search.propTypes = propTypes;

export { Search };

export default Search;
